import React, { useState } from 'react';
import { Button, Input, Tag, Typography, message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { PatientTreatmentPlan, PatientTreatmentPlanStageLog } from '../../../../../types/healthcare/TreatmentPlanTypes';
import { triggerComprehensiveAnalysis } from '../../../../../api/healthcare/TreatmentPlanApi';

const { Text } = Typography;

const panelStyle: React.CSSProperties = {
  border: '1px solid var(--mg-border)',
  borderRadius: 6,
  padding: '8px 10px',
  background: 'var(--mg-bg-card, var(--mg-bg))',
};

const sectionLabel: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--mg-text-secondary)',
  display: 'block',
  marginBottom: 6,
};

interface ComprehensiveAnalysisInputRendererProps {
  plan: PatientTreatmentPlan;
  activeLog: PatientTreatmentPlanStageLog;
  onComplete: () => void;
}

const ComprehensiveAnalysisInputRenderer: React.FC<ComprehensiveAnalysisInputRendererProps> = ({
  plan,
  activeLog,
  onComplete,
}) => {
  const [extraContext, setExtraContext] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const outcomeData = activeLog.outcome_data ?? {};
  const dataSources: string[] = outcomeData.data_sources ?? [];
  const includeMedication: boolean = outcomeData.include_medication ?? false;

  const handleStart = async () => {
    setSubmitting(true);
    try {
      await triggerComprehensiveAnalysis(plan.id, activeLog.id, extraContext.trim() || null);
      await queryClient.invalidateQueries({ queryKey: ['patient-treatment-plan', plan.id] });
      onComplete();
    } catch {
      message.error('Failed to trigger analysis. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

      <div style={panelStyle}>
        <Text style={sectionLabel}>Practitioner Notes (optional)</Text>
        <Input.TextArea
          value={extraContext}
          onChange={(e) => setExtraContext(e.target.value)}
          placeholder="Add any clinical context, concerns, or notes for the AI to consider during analysis…"
          autoSize={{ minRows: 3, maxRows: 8 }}
          style={{ fontSize: 12 }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" size="small" loading={submitting} onClick={handleStart}>
          Start Analysis
        </Button>
      </div>
    </div>
  );
};

export default ComprehensiveAnalysisInputRenderer;
